// Note: Several mutation endpoints use GET instead of POST/PUT.
// This matches the legacy backend API contract which only accepts GET for these routes.
import { api } from '@core/api';

export interface ModifyTimePolicyResponse {
  canModify: boolean;
  cancellationPolicyHours: number;
}

export interface AvailableTimeSlot {
  hour: string;
  areaId: number;
  available: boolean;
  restrictionTypes: string[];
}

interface AvailableTimesResponse {
  data: AvailableTimeSlot[];
}

interface SubmitTimeChangeResponse {
  date: string;
}

export async function checkModifyTimePolicy(
  reservationId: number,
): Promise<ModifyTimePolicyResponse> {
  return api.get<ModifyTimePolicyResponse>('/api/get-modify-time-policy', {
    params: { reservation: reservationId },
  });
}

export async function fetchAvailableTimesForEdition(
  subdomain: string,
  reservationId: number,
): Promise<AvailableTimeSlot[]> {
  const response = await api.get<AvailableTimesResponse>(
    `/api/rest/v2/restaurants/${subdomain}/availabilities/availabletimesforedition`,
    { params: { reservationId } },
  );
  return response.data;
}

// --- Change Size ---

export interface PartySizeInfo {
  available: boolean;
  areaId: number;
}

interface AvailableSizesResponse {
  data: PartySizeInfo[];
}

export async function fetchAvailableSizesForEdition(
  subdomain: string,
  reservationId: number,
): Promise<PartySizeInfo[]> {
  const response = await api.get<AvailableSizesResponse>(
    `/api/rest/v2/restaurants/${subdomain}/availabilities/availablesizesforedition`,
    { params: { reservationId } },
  );
  return response.data;
}

export async function submitSizeChange(
  reservationId: number,
  newPartySize: number,
  areaId: number,
): Promise<void> {
  await api.get('/api/edit_reservation_partysize', {
    params: { reservation: reservationId, newPartySize, area: areaId },
  });
}

// --- Change Name ---

export async function submitNameChange(
  reservationId: number,
  guestName: string,
): Promise<string> {
  return api.get<string>('/api/save_reservation_guest_name', {
    params: { id: reservationId, guest_name: guestName },
  });
}

// --- Cancel Reservation ---

export async function cancelReservation(
  lang: string,
  reservationId: number,
): Promise<void> {
  await api.post(`/api/${lang}/user-cancel-reservation`, { resId: reservationId });
}

export async function markWontAttend(
  reservationId: number,
): Promise<void> {
  await api.get('/api/wont-attend', {
    params: { reservation: reservationId },
  });
}

// --- Change Time ---

export async function submitTimeChange(
  reservationId: number,
  newTime: string,
  areaId: number,
): Promise<SubmitTimeChangeResponse> {
  return api.get<SubmitTimeChangeResponse>('/api/edit-reservation-time', {
    params: { reservation: reservationId, newResTime: newTime, area: areaId },
  });
}
